import { useState } from "react";
import { motion } from "framer-motion";
import { UserGear, Strategy, UsersThree } from "@phosphor-icons/react";

const programs = [
  {
    icon: UserGear,
    name: "For Managers",
    lead: "Your managers make hundreds of decisions a day. Most could be better informed — if they knew how to use AI as a thinking partner, not a shortcut.",
    topics: [
      "Using AI to cut through noise and find what matters in reports and feedback",
      "Running performance conversations backed by patterns, not gut feel",
      "Planning in real time instead of relying on static quarterly plans",
      "Knowing when to trust AI and when to use their own judgement",
    ],
  },
  {
    icon: Strategy,
    name: "For Leaders",
    lead: "Leaders don't need to learn prompts. They need to understand what changes when AI enters the operating model — and what stays the same.",
    topics: [
      "Where AI creates real value vs where it creates noise",
      "Rethinking team structure when parts of the work are automated",
      "Reading AI-generated recommendations without outsourcing judgement",
      "Making investment decisions about AI that survive the next budget cycle",
    ],
  },
  {
    icon: UsersThree,
    name: "For HR Teams",
    lead: "Your HR team will either lead the AI shift in your organisation — or be reorganised by it. This programme makes sure it's the former.",
    topics: [
      "Mapping skills and succession using structured analysis, not guesswork",
      "Turning engagement data and exit interviews into actionable signals",
      "Building prompts and workflows for real HR tasks — not generic demos",
      "Presenting people insights to the board with data, not slide decks",
    ],
  },
];

export default function Training() {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <section
      id="training"
      data-testid="training-section"
      className="relative py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="overline mb-5">Training</p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.15] max-w-3xl"
            style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
          >
            Get your team <span className="metallic-text">ready</span> for AI.
          </h2>
          <p className="mt-6 text-base sm:text-lg max-w-2xl leading-relaxed text-themed-body">
            Three programmes, three audiences. Each one designed to change how
            your people think about AI — not just how they use it.
            Every scenario is drawn from real HR work, not generic examples.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {programs.map((prog, i) => (
            <motion.div
              key={prog.name}
              data-testid={`training-card-${i}`}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              className="glass-card p-7 sm:p-8 flex flex-col"
            >
              <prog.icon
                size={26}
                weight="regular"
                className="mb-5"
                style={{
                  color: hoveredIdx === i ? "var(--sv-gold)" : "var(--sv-text-muted)",
                  transition: "color 0.3s ease",
                }}
              />

              <h3
                className="text-xl font-semibold mb-4"
                style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
              >
                {prog.name}
              </h3>

              <p className="text-sm leading-relaxed text-themed-body mb-6">
                {prog.lead}
              </p>

              <div className="mt-auto">
                <span
                  className="block text-[10px] tracking-[0.15em] uppercase mb-3"
                  style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-text-muted)" }}
                >
                  What we cover
                </span>
                <ul className="space-y-3">
                  {prog.topics.map((t, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <span className="accent-dot mt-1.5" />
                      <span className="text-sm leading-relaxed text-themed-muted">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          className="mt-12 text-sm italic text-center"
          style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-muted)" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          "Every programme is built around your organisation's own policies, systems and context."
        </motion.p>
      </div>
    </section>
  );
}
